import React, { useState, useEffect, useMemo } from 'react';
import { api as axios } from '../utils/apiClient';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import { isUpdateAvailable, hasWordPressReportedUpdate } from '../utils/pluginUtils';
import { useUpdatePlugin } from '../hooks/useUpdatePlugin';
import './SiteDetails.css';

const AllPlugins = () => {
  const [sites, setSites] = useState([]);
  const [pluginsData, setPluginsData] = useState({}); // { siteId: { plugins: [], summary: {}, ... } }
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);

  // Filter and search states
  const [selectedSite, setSelectedSite] = useState('all'); // 'all' or siteId
  const [filter, setFilter] = useState('all'); // all, active, inactive, update_available
  const [searchQuery, setSearchQuery] = useState('');
  const [updateModal, setUpdateModal] = useState({
    open: false,
    plugin: null,
    slug: null,
    loading: false,
    actions: null,
    error: null
  });
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [copyStatus, setCopyStatus] = useState(null);
  // For AllPlugins, we don't pass siteId to hook since we have multiple sites
  // updatePlugin will accept siteId as second parameter
  const { updatePlugin, isUpdatingTarget } = useUpdatePlugin();
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString();
  };

  const formatCreatedDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return {
      date: parsed.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }),
      time: parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })
    };
  };

  const resolveLastUpdated = (plugin) => {
    return formatDate(
      plugin?.last_updated ||
      plugin?.wporg_last_updated ||
      plugin?.UpdateDate ||
      plugin?.lastUpdated
    );
  };

  const resolveCompatible = (plugin) => {
    return plugin?.compatible_up_to ||
      plugin?.tested ||
      plugin?.update?.tested ||
      'N/A';
  };

  const isCompatibleBelowEight = (value) => {
    if (!value || value === 'N/A') return false;
    const numeric = parseFloat(String(value).replace(/[^0-9.]/g, ''));
    if (Number.isNaN(numeric)) return false;
    return numeric < 8;
  };

  const getPluginSlug = (plugin) => {
    if (!plugin) return null;
    const candidates = [
      plugin.slug,
      plugin.plugin,
      plugin.plugin_file,
      plugin.text_domain,
      plugin.name
    ];
    for (const candidate of candidates) {
      if (!candidate) continue;
      const sanitized = String(candidate).trim().toLowerCase();
      if (!sanitized || sanitized === '.') continue;
      return sanitized.split('/')[0];
    }
    return null;
  };

  const logUpdateAction = (siteId, slug, actionType) => {
    axios.post(`/api/sites/${siteId}/plugins/${slug}/update-action-log`, { actionType }).catch(() => {
      console.warn('Unable to log update action');
    });
  };

  const closeUpdateModal = () => {
    setUpdateModal({
      open: false,
      plugin: null,
      slug: null,
      loading: false,
      actions: null,
      error: null
    });
    setCopyStatus(null);
  };

  const openUpdateModal = async (plugin) => {
    const slug = getPluginSlug(plugin);
    if (!slug) {
      setUpdateModal({
        open: true,
        plugin,
        slug: null,
        loading: false,
        actions: null,
        error: 'Unable to determine plugin slug from the monitoring data.'
      });
      return;
    }
    setUpdateModal({
      open: true,
      plugin,
      slug,
      loading: true,
      actions: null,
      error: null
    });
    try {
      const response = await axios.get(`/api/sites/${plugin.siteId}/plugins/${slug}/update-actions`);
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Unable to load update actions.');
      }
      setUpdateModal((prev) => ({
        ...prev,
        loading: false,
        actions: response.data
      }));
    } catch (err) {
      setUpdateModal((prev) => ({
        ...prev,
        loading: false,
        error: err.response?.data?.message || err.message || 'Unable to load update actions.'
      }));
    }
  };

  const handleOpenAdmin = (url, siteId, slug, actionType) => {
    if (!url) return;
    logUpdateAction(siteId, slug, actionType);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopyWpCli = async (command, siteId, slug) => {
    if (!command) return;
    try {
      await navigator.clipboard.writeText(command);
      setCopyStatus('wpcli');
      logUpdateAction(siteId, slug, 'copy_wpcli');
      setTimeout(() => setCopyStatus(null), 2000);
    } catch (err) {
      console.warn('Clipboard copy failed', err);
    }
  };

  const handleAgentDownload = (url, siteId, slug) => {
    if (!url) return;
    logUpdateAction(siteId, slug, 'download_agent');
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Handle update success/error from hook
  useEffect(() => {
    // This will be handled by the hook's internal state
    // We'll refresh on success via the hook's success state
  }, []);

  useEffect(() => {
    fetchAllPlugins();
    
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchAllPlugins(true);
      }, refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  const fetchAllPlugins = async (showLoading = true, forceRefresh = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      // First, fetch all sites
      const sitesResponse = await axios.get('/api/sites');
      const sitesList = sitesResponse.data;
      setSites(sitesList);

      // Then fetch plugins for each site
      const pluginPromises = sitesList.map(async (site) => {
        try {
          const url = `/api/sites/${site._id}/plugins${forceRefresh ? '?force=true' : ''}`;
          const response = await axios.get(url);
          
          // Comprehensive response handling - match the same logic as Plugins.js
          if (!response || !response.data) {
            console.warn(`⚠️ No response data for site ${site._id} (${site.name})`);
            return {
              siteId: site._id,
              siteName: site.name,
              siteUrl: site.url,
              data: null,
              error: 'No response data from server'
            };
          }

          let pluginsArray = null;
          let summaryData = null;

          // Check 1: Standard format with status='ok' and plugins array
          if (response.data.status === 'ok') {
            if (response.data.plugins !== undefined) {
              pluginsArray = Array.isArray(response.data.plugins) ? response.data.plugins : [];
              summaryData = response.data.summary || {
                total: pluginsArray.length,
                active: pluginsArray.filter(p => p.active || p.status === 'active').length,
                inactive: pluginsArray.filter(p => !p.active && p.status !== 'active').length,
                updates: pluginsArray.filter(p => hasWordPressReportedUpdate(p)).length
              };
            } else {
              // status='ok' but no plugins field - treat as empty
              pluginsArray = [];
              summaryData = { total: 0, active: 0, inactive: 0, updates: 0 };
            }
          }
          // Check 2: Format with success=true and plugins array
          else if (response.data.success && response.data.plugins) {
            pluginsArray = Array.isArray(response.data.plugins) ? response.data.plugins : [];
            summaryData = {
              total: response.data.total || pluginsArray.length,
              active: response.data.active_count || pluginsArray.filter(p => p.active || p.status === 'active').length,
              inactive: response.data.inactive_count || pluginsArray.filter(p => !p.active && p.status !== 'active').length,
              updates: response.data.update_count || response.data.summary?.updates || pluginsArray.filter(p => p.hasUpdate === true).length
            };
          }
          // Check 3: Direct array format
          else if (Array.isArray(response.data)) {
            pluginsArray = response.data;
            summaryData = {
              total: pluginsArray.length,
              active: pluginsArray.filter(p => p.active || p.status === 'active').length,
              inactive: pluginsArray.filter(p => !p.active && p.status !== 'active').length,
              updates: pluginsArray.filter(p => p.hasUpdate === true).length
            };
          }
          // Check 4: Error response - but check if we have cached/warning data
          else if (response.data.status === 'error' || response.data.status === 'cached') {
            const errorMessage = response.data.errorMessage || 'Failed to fetch plugins';
            const suggestions = response.data.suggestions || [];
            
            // If status is 'cached', we still have plugin data to show
            if (response.data.status === 'cached' && response.data.plugins && Array.isArray(response.data.plugins)) {
              return {
                siteId: site._id,
                siteName: site.name,
                siteUrl: site.url,
                data: {
                  status: 'cached',
                  plugins: response.data.plugins,
                  summary: response.data.summary || {
                    total: response.data.plugins.length,
                    active: response.data.plugins.filter(p => p.active || p.status === 'active').length,
                    inactive: response.data.plugins.filter(p => !p.active && p.status !== 'active').length,
                    updates: 0
                  },
                  warning: response.data.warning || 'Using cached data'
                },
                error: errorMessage, // Still show the error message
                errorDetails: response.data,
                suggestions: suggestions
              };
            }
            
            return {
              siteId: site._id,
              siteName: site.name,
              siteUrl: site.url,
              data: null,
              error: errorMessage,
              errorDetails: response.data,
              suggestions: suggestions
            };
          }
          // Check 5: Try to extract plugins from unexpected locations
          else if (response.data && typeof response.data === 'object') {
            const possibleKeys = ['plugins', 'data', 'items', 'results'];
            for (const key of possibleKeys) {
              if (response.data[key] && Array.isArray(response.data[key])) {
                pluginsArray = response.data[key];
                summaryData = {
                  total: pluginsArray.length,
                  active: pluginsArray.filter(p => p.active || p.status === 'active').length,
                  inactive: pluginsArray.filter(p => !p.active && p.status !== 'active').length,
                  updates: pluginsArray.filter(p => p.update || p.hasUpdate).length
                };
                break;
              }
            }
          }

          // If we successfully extracted plugins, return the data
          if (pluginsArray !== null && Array.isArray(pluginsArray)) {
            return {
              siteId: site._id,
              siteName: site.name,
              siteUrl: site.url,
              data: {
                status: 'ok',
                plugins: pluginsArray,
                summary: summaryData || {
                  total: pluginsArray.length,
                  active: 0,
                  inactive: 0,
                  updates: 0
                }
              }
            };
          }

          // If we get here, we couldn't parse the response
          console.warn(`⚠️ Could not parse response for site ${site._id} (${site.name}):`, {
            hasData: !!response.data,
            dataType: typeof response.data,
            isArray: Array.isArray(response.data),
            dataKeys: response.data ? Object.keys(response.data) : [],
            status: response.data?.status,
            success: response.data?.success,
            hasPlugins: !!response.data?.plugins,
            pluginsType: typeof response.data?.plugins
          });

          return {
            siteId: site._id,
            siteName: site.name,
            siteUrl: site.url,
            data: null,
            error: 'Invalid response format',
            errorDetails: {
              message: 'Could not parse plugin data from server response',
              responseKeys: response.data ? Object.keys(response.data) : [],
              responseType: typeof response.data
            }
          };
        } catch (err) {
          console.error(`❌ Error fetching plugins for site ${site._id} (${site.name}):`, err);
          const errorData = err.response?.data || {};
          return {
            siteId: site._id,
            siteName: site.name,
            siteUrl: site.url,
            data: null,
            error: errorData.errorMessage || errorData.error || err.message || 'Failed to fetch plugins',
            errorDetails: errorData,
            suggestions: errorData.suggestions || []
          };
        }
      });

      const results = await Promise.all(pluginPromises);
      const pluginsMap = {};
      results.forEach((result) => {
        pluginsMap[result.siteId] = {
          siteName: result.siteName,
          siteUrl: result.siteUrl,
          ...result.data,
          error: result.error
        };
      });

      setPluginsData(pluginsMap);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching plugins:', err);
      setError(err.response?.data?.errorMessage || err.message || 'Failed to load plugins');
    } finally {
      setLoading(false);
    }
  };

  const syncSitePlugins = async (siteId) => {
    try {
      setSyncing({ ...syncing, [siteId]: true });
      await fetchAllPlugins(false, true);
      setSyncing({ ...syncing, [siteId]: false });
    } catch (err) {
      console.error(`Error syncing plugins for site ${siteId}:`, err);
      setSyncing({ ...syncing, [siteId]: false });
    }
  };

  // Combine all plugins with site information
  const allPluginsWithSite = useMemo(() => {
    const allPlugins = [];
    Object.keys(pluginsData).forEach((siteId) => {
      const siteData = pluginsData[siteId];
      // Include plugins even if there's an error but we have cached data
      if (siteData && siteData.plugins && Array.isArray(siteData.plugins) && siteData.plugins.length > 0) {
        siteData.plugins.forEach((plugin) => {
          allPlugins.push({
            ...plugin,
            siteId,
            siteName: siteData.siteName,
            siteUrl: siteData.siteUrl
          });
        });
      }
    });
    return allPlugins;
  }, [pluginsData]);

  // Filter plugins
  const filteredPlugins = useMemo(() => {
    let filtered = allPluginsWithSite;

    // Filter by site
    if (selectedSite !== 'all') {
      filtered = filtered.filter(p => p.siteId === selectedSite);
    }

    // Filter by status
    if (filter === 'active') {
      filtered = filtered.filter(p => p.active || p.status === 'active');
    } else if (filter === 'inactive') {
      filtered = filtered.filter(p => !p.active && p.status !== 'active');
    } else if (filter === 'update_available') {
      // Use WordPress-only update detection for filtering to match backend count
      filtered = filtered.filter(p => hasWordPressReportedUpdate(p));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        (p.name && p.name.toLowerCase().includes(query)) ||
        (p.description && p.description.toLowerCase().includes(query)) ||
        (p.author && p.author.toLowerCase().includes(query)) ||
        (p.siteName && p.siteName.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [allPluginsWithSite, selectedSite, filter, searchQuery]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const total = allPluginsWithSite.length;
    const active = allPluginsWithSite.filter(p => p.active || p.status === 'active').length;
    const inactive = total - active;
    // Use WordPress-only update detection for count to match backend exactly
    const updates = allPluginsWithSite.filter(p => hasWordPressReportedUpdate(p)).length;

    return { total, active, inactive, updates };
  }, [allPluginsWithSite]);

  const renderUpdateModal = () => {
    if (!updateModal.open) return null;
    const { plugin, slug, actions, loading: modalLoading, error: modalError } = updateModal;
    const agentDetails = actions?.agent || {};
    return (
      <div className="update-modal-backdrop">
        <div className="update-modal">
          <button className="modal-close" onClick={closeUpdateModal} aria-label="Close update modal">
            ×
          </button>
          <h2>Update {plugin?.name || slug}</h2>
          <p className="update-modal-warning">
            WebMonitor cannot and will not update plugins remotely. Use one of these safe actions from your WordPress
            server or admin dashboard.
          </p>
          {modalLoading && <p>Loading update actions…</p>}
          {modalError && !modalLoading && (
            <div className="error-message">
              <p>{modalError}</p>
            </div>
          )}
          {!modalLoading && !modalError && actions && (
            <div className="update-action-grid">
              <div className="update-action-card">
                <h3>Open WP Admin</h3>
                <p>Use WordPress admin on {plugin?.siteName || plugin?.siteUrl} to run the update directly.</p>
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => handleOpenAdmin(actions.wpAdminPluginList, plugin.siteId, slug, 'open_admin_list')}
                >
                  Open plugin list
                </button>
                <button
                  className="ghost-button"
                  type="button"
                  onClick={() => handleOpenAdmin(actions.wpAdminUrl, plugin.siteId, slug, 'open_admin_details')}
                >
                  Open plugin details
                </button>
                <small className="update-note">Admin URL: {actions.wpAdminPluginList}</small>
              </div>

              <div className="update-action-card">
                <h3>Copy WP-CLI command</h3>
                <p>Run this from the server (after taking backups and using the correct user).</p>
                <pre className="code-block">{actions.wpCliCommand}</pre>
                <button
                  className="ghost-button"
                  type="button"
                  onClick={() => handleCopyWpCli(actions.wpCliCommand, plugin.siteId, slug)}
                >
                  {copyStatus === 'wpcli' ? 'Copied!' : 'Copy command'}
                </button>
                <small className="update-note">
                  Example backup: <code>wp db export backup.sql && tar -czf wp-files.tar.gz /var/www/html</code>
                </small>
              </div>

              <div className="update-action-card">
                <h3>Install agent (manual & optional)</h3>
                <p>
                  Download the signed helper plugin if you want to prepare for automation later. It must be installed
                  manually by a site admin.
                </p>
                <button
                  className="ghost-button"
                  type="button"
                  onClick={() => handleAgentDownload(agentDetails.downloadUrl, plugin.siteId, slug)}
                >
                  Download agent ZIP
                </button>
                <p className="token-label">
                  Token: <code>{agentDetails.token || 'generated-per-download'}</code>
                </p>
                <ol className="update-note">
                  <li>Log into WordPress → Plugins → Add New → Upload Plugin.</li>
                  <li>Activate “WebMonitor Update Agent (Manual)”.</li>
                  <li>Open Tools → WebMonitor Agent and paste the token above.</li>
                </ol>
                <small className="update-note">
                  Security notice: this plugin stores your token locally and does not grant remote control unless you
                  choose to use it.
                </small>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderHelpModal = () => {
    if (!showHelpModal) return null;
    return (
      <div className="update-modal-backdrop">
        <div className="update-modal">
          <button className="modal-close" onClick={() => setShowHelpModal(false)} aria-label="Close help modal">
            ×
          </button>
          <h2>Why updates are read-only</h2>
          <p>
            WebMonitor connects to WordPress using Application Passwords that only allow reading data. Running updates
            remotely would require uploading code and granting full filesystem access, which is outside the scope of this
            dashboard.
          </p>
          <p>Use one of these flows instead:</p>
          <ul className="update-note">
            <li>Open WordPress admin from WebMonitor and click update.</li>
            <li>Run the provided WP-CLI command on the server after taking backups.</li>
            <li>Install the optional agent plugin manually if you want to prepare for automation in the future.</li>
          </ul>
          <p>These flows keep control on your server so you can audit every change.</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="app-container">
        <Sidebar />
        <div className="main-content">
          <div className="loading">Loading plugins...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-content">
          {toast && (
            <div className={`toast toast-${toast.type}`}>
              {toast.message}
            </div>
          )}
        <div className="plugins-page">
          <div className="plugins-header">
            <h1>All Plugins</h1>
            <div className="plugins-summary">
              <span>{summary.total} items</span>
              {lastUpdated && (
                <span>Last checked: {new Date(lastUpdated).toLocaleString()}</span>
              )}
            </div>
          </div>

          <div className="plugins-controls">
            <div className="controls-left">
              <label>
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
                Auto-refresh
              </label>
              {autoRefresh && (
                <select
                  value={refreshInterval}
                  onChange={(e) => setRefreshInterval(Number(e.target.value))}
                  style={{ marginLeft: '10px' }}
                >
                  <option value={10}>10 seconds</option>
                  <option value={30}>30 seconds</option>
                  <option value={60}>1 minute</option>
                  <option value={300}>5 minutes</option>
                </select>
              )}
              <button
                className="btn-refresh"
                onClick={() => fetchAllPlugins(true, true)}
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            <div className="controls-right">
              <select
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
                style={{ marginRight: '10px', padding: '5px' }}
              >
                <option value="all">All Sites</option>
                {sites.map(site => (
                  <option key={site._id} value={site._id}>{site.name}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Search plugins..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ padding: '5px', width: '200px' }}
              />
              <button
                className="ghost-button help-button"
                type="button"
                onClick={() => setShowHelpModal(true)}
              >
                Why read-only?
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <p>⚠️ Error: {error}</p>
            </div>
          )}

          <div className="plugins-filters">
            <button
              className={filter === 'all' ? 'active' : ''}
              onClick={() => setFilter('all')}
            >
              All ({summary.total})
            </button>
            <button
              className={filter === 'active' ? 'active' : ''}
              onClick={() => setFilter('active')}
            >
              Active ({summary.active})
            </button>
            <button
              className={filter === 'inactive' ? 'active' : ''}
              onClick={() => setFilter('inactive')}
            >
              Inactive ({summary.inactive})
            </button>
            <button
              className={filter === 'update_available' ? 'active' : ''}
              onClick={() => setFilter('update_available')}
            >
              Update Available ({summary.updates})
            </button>
          </div>

          <div className="plugin-card-grid compact">
            {filteredPlugins.length === 0 ? (
              <div className="no-plugins">
                <p>No plugins found matching your criteria.</p>
              </div>
            ) : (
              filteredPlugins.map((plugin, index) => {
                const pluginKey = `${plugin.siteId}-${plugin.plugin || plugin.plugin_file || plugin.slug || plugin.name || index}`;
                const lastUpdated = resolveLastUpdated(plugin);
                const compatible = resolveCompatible(plugin);
                const compatibleWarning = isCompatibleBelowEight(compatible);
                const isActive = plugin.active || plugin.status === 'active';
                const slug = getPluginSlug(plugin);
                const hasSlug = !!slug;
                // REAL WORDPRESS UPDATES ONLY:
                // Use hasWordPressReportedUpdate() so we only show updates when
                // WordPress core actually reports update.new_version metadata.
                const updateAvailable = hasWordPressReportedUpdate(plugin);
                const isRowUpdating = updateAvailable && hasSlug && isUpdatingTarget(slug, plugin.siteId);
                
                // Debug log to verify update status for ALL plugins
                console.debug(
                  `[AllPlugins Update Check] slug=${slug || plugin.slug || plugin.plugin} ` +
                  `local_version=${plugin.version || plugin.Version} ` +
                  `new_version=${plugin.update?.new_version || plugin.update?.newVersion || 'n/a'} ` +
                  `hasUpdateFlag=${plugin.hasUpdate === true} ` +
                  `updateAvailable=${updateAvailable}`
                );
                
                return (
                  <div className="plugin-card compact" key={pluginKey}>
                    <div className="plugin-card-head">
                      <span className="badge-site">{plugin.siteName || plugin.siteUrl}</span>
                      <div className="plugin-head-actions">
                        <span className={`status-pill ${isActive ? 'active' : 'inactive'}`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                        {updateAvailable && hasSlug && (
                          <button
                            className={`pill pill-update ${isRowUpdating ? 'loading' : ''}`}
                            type="button"
                            onClick={(e) => {
                              if (e && e.preventDefault) e.preventDefault();
                              if (e && e.stopPropagation) e.stopPropagation();
                              console.log('[AllPlugins] Update button clicked', { name: plugin.name, slug, siteId: plugin.siteId, isUpdateAvailable: plugin.isUpdateAvailable, hasUpdate: plugin.hasUpdate });
                              // Call updatePlugin with slug and siteId - only shown when updateAvailable === true
                              updatePlugin(slug, plugin.siteId).then((result) => {
                                if (result?.success) {
                                  showToast(result.message || 'Plugin updated successfully', 'success');
                                  setTimeout(async () => {
                                    await fetchAllPlugins(false, true);
                                    window.dispatchEvent(new Event('plugin-updated'));
                                  }, 500);
                                } else {
                                  showToast(`Update failed: ${result?.error || 'Unexpected error'}`, 'error');
                                }
                              }).catch((error) => {
                                showToast(`Update failed: ${error.message || 'Unexpected error'}`, 'error');
                              });
                            }}
                            disabled={isRowUpdating}
                          >
                            {isRowUpdating ? (
                              <>
                                <span className="button-spinner" aria-hidden="true"></span>
                                <span className="spinner-text">Updating…</span>
                              </>
                            ) : (
                              'Update'
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="plugin-card-title-row">
                      <div>
                        <h3>{plugin.name || 'Unknown Plugin'}</h3>
                        <p>{plugin.description || 'No description available.'}</p>
                      </div>
                    </div>

                    <div className="metric-row">
                      <div className="metric-tile">
                        <span className="metric-label">Version</span>
                        <span className="metric-value">{plugin.version || 'N/A'}</span>
                      </div>
                      <div className={`metric-tile ${compatibleWarning ? 'metric-warning' : ''}`}>
                        <span className="metric-label">Compatible up to</span>
                        <span className="metric-value">{compatible}</span>
                      </div>
                    </div>

                    <div className="metric-row">
                      <div className="metric-tile full">
                        <span className="metric-label">Last Updated</span>
                        <span className="metric-value">{lastUpdated}</span>
                      </div>
                    </div>

                    <div className="metric-row">
                      <div className="metric-tile full">
                        <span className="metric-label">Created Date</span>
                        {(() => {
                          // Use createdDate from enrichment, fallback to installed_at
                          const createdDate = formatCreatedDate(plugin.createdDate || plugin.installed_at);
                          if (createdDate) {
                            return (
                              <div className="metric-value" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <span>{createdDate.date}</span>
                                <span style={{ fontSize: '0.85em', color: '#666' }}>{createdDate.time}</span>
                              </div>
                            );
                          }
                          return <span className="metric-value">N/A</span>;
                        })()}
                      </div>
                    </div>

                    <div className="metric-row">
                      <div className="metric-tile full">
                        <span className="metric-label">Update Available</span>
                        {(() => {
                          const updateAvailableDate = formatCreatedDate(plugin.updateAvailableDate);
                          if (updateAvailableDate) {
                            return (
                              <div className="metric-value" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <span>{updateAvailableDate.date}</span>
                                <span style={{ fontSize: '0.85em', color: '#666' }}>{updateAvailableDate.time}</span>
                              </div>
                            );
                          }
                          return <span className="metric-value">-</span>;
                        })()}
                      </div>
                    </div>

                    <div className="metric-row">
                      <div className="metric-tile full">
                        <span className="metric-label">Update Completed</span>
                        {(() => {
                          const updateCompletedDate = formatCreatedDate(plugin.updateCompletedDate);
                          if (updateCompletedDate) {
                            return (
                              <div className="metric-value" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <span>{updateCompletedDate.date}</span>
                                <span style={{ fontSize: '0.85em', color: '#666' }}>{updateCompletedDate.time}</span>
                              </div>
                            );
                          }
                          return <span className="metric-value">-</span>;
                        })()}
                      </div>
                    </div>

                    <div className="plugin-card-actions compact">
                      <Link
                        to={`/sites/${plugin.siteId}`}
                        state={{ activeTab: 'plugins' }}
                        className="primary-button"
                      >
                        View Details
                      </Link>
                      <button className="ghost-button" onClick={() => syncSitePlugins(plugin.siteId)}>
                        Sync
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Show site-specific errors */}
          {Object.keys(pluginsData).map(siteId => {
            const siteData = pluginsData[siteId];
            // Show error if there's an error AND no plugin data (or empty plugin data)
            const hasError = siteData && siteData.error && (!siteData.plugins || siteData.plugins.length === 0);
            const hasWarning = siteData && siteData.warning && siteData.plugins && siteData.plugins.length > 0;
            
            if (hasError || hasWarning) {
              // Determine if this is a warning (has data but error) or full error
              const isWarning = hasWarning && siteData.plugins && siteData.plugins.length > 0;
              
              // Determine error type for better styling
              const isAuthError = siteData.error && (siteData.error.toLowerCase().includes('credential') || 
                                 siteData.error.toLowerCase().includes('authentication') ||
                                 siteData.error.toLowerCase().includes('forbidden') ||
                                 siteData.error.toLowerCase().includes('401') ||
                                 siteData.error.toLowerCase().includes('403'));
              const isPluginError = siteData.error && (siteData.error.toLowerCase().includes('plugin') ||
                                   siteData.error.toLowerCase().includes('endpoint') ||
                                   siteData.error.toLowerCase().includes('404') ||
                                   siteData.error.toLowerCase().includes('unreachable'));
              
              // Use warning colors if we have cached data
              const errorColor = isWarning ? '#856404' : (isAuthError ? '#721c24' : isPluginError ? '#856404' : '#856404');
              const errorBg = isWarning ? '#fff3cd' : (isAuthError ? '#f8d7da' : isPluginError ? '#fff3cd' : '#fff3cd');
              const errorBorder = isWarning ? '#ffc107' : (isAuthError ? '#f5c6cb' : isPluginError ? '#ffc107' : '#ffc107');

              return (
                <div key={siteId} className="error-message" style={{ 
                  marginTop: '10px', 
                  padding: '15px', 
                  background: errorBg, 
                  border: `1px solid ${errorBorder}`, 
                  borderRadius: '4px' 
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '1.2em' }}>⚠️</span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ color: errorColor }}>
                        {isWarning ? '⚠️ Warning' : '❌ Error'} for {siteData.siteName || siteData.siteUrl}:
                      </strong>
                      {isWarning && siteData.warning && (
                        <p style={{ margin: '5px 0', color: '#856404', fontWeight: '500', fontStyle: 'italic' }}>
                          {siteData.warning} ({siteData.plugins?.length || 0} plugins available)
                        </p>
                      )}
                      {siteData.error && (
                        <p style={{ margin: '5px 0', color: errorColor, fontWeight: '500' }}>{siteData.error}</p>
                      )}
                      
                      {/* Show suggestions if available */}
                      {siteData.suggestions && siteData.suggestions.length > 0 && (
                        <div style={{ marginTop: '10px', padding: '10px', background: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                          <strong style={{ color: errorColor, fontSize: '0.9em' }}>💡 How to Fix:</strong>
                          <ul style={{ margin: '5px 0', paddingLeft: '20px', color: '#666', fontSize: '0.9em' }}>
                            {siteData.suggestions.map((suggestion, idx) => (
                              <li key={idx} style={{ marginBottom: '5px' }}>{suggestion}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Quick action buttons based on error type */}
                      <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {isAuthError && (
                          <Link
                            to={`/sites/${siteId}`}
                            style={{
                              padding: '8px 16px',
                              background: '#007bff',
                              color: 'white',
                              textDecoration: 'none',
                              borderRadius: '4px',
                              fontSize: '0.9em',
                              fontWeight: '500',
                              display: 'inline-block'
                            }}
                          >
                            🔧 Configure Credentials
                          </Link>
                        )}
                        {isPluginError && (
                          <>
                            <div style={{ 
                              fontSize: '0.9em', 
                              color: '#666', 
                              padding: '8px 12px', 
                              background: '#fff', 
                              borderRadius: '4px',
                              border: '1px solid #ddd'
                            }}>
                              📦 Install plugin on: <strong style={{ color: '#856404' }}>{siteData.siteUrl}</strong>
                            </div>
                            <a
                              href="/wordpress-plugin-webmonitor.php"
                              download="wordpress-plugin-webmonitor.php"
                              style={{
                                padding: '8px 16px',
                                background: '#28a745',
                                color: 'white',
                                textDecoration: 'none',
                                borderRadius: '4px',
                                fontSize: '0.9em',
                                fontWeight: '500',
                                display: 'inline-block'
                              }}
                            >
                              ⬇️ Download Plugin File
                            </a>
                          </>
                        )}
                      </div>

                      {siteData.errorDetails && (
                        <details style={{ marginTop: '10px', fontSize: '0.85em', color: '#666' }}>
                          <summary style={{ cursor: 'pointer', color: errorColor, fontWeight: '500' }}>View Technical Details</summary>
                          <pre style={{ marginTop: '10px', padding: '10px', background: '#fff', border: '1px solid #ddd', borderRadius: '4px', overflow: 'auto', maxHeight: '200px', fontSize: '0.8em' }}>
                            {JSON.stringify(siteData.errorDetails, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => syncSitePlugins(siteId)}
                    disabled={syncing[siteId]}
                    style={{ 
                      marginTop: '5px', 
                      padding: '8px 16px', 
                      background: errorBorder,
                      color: errorColor,
                      border: `1px solid ${errorBorder}`,
                      borderRadius: '4px',
                      cursor: syncing[siteId] ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      opacity: syncing[siteId] ? 0.6 : 1
                    }}
                  >
                    {syncing[siteId] ? 'Syncing...' : 'Retry'}
                  </button>
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
      {renderUpdateModal()}
      {renderHelpModal()}
    </div>
  );
};

export default AllPlugins;

